import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import type { PreferenceMap, Rating, SetPreferenceMap } from "../lib/preferences";
import type { Route } from "../lib/router";

type LoadState =
  | { kind: "loading" }
  | { kind: "loaded"; eventSlug: string; picks: PreferenceMap }
  | { kind: "notfound" }
  | { kind: "error"; message: string };

/**
 * Resolves a /s/:token short URL. Fetches the share, then navigates to
 * the event's schedule view with the picks pre-loaded into sessionStorage.
 * Schedule view picks them up and offers the import banner.
 */
export function ShareLanding({
  token,
  navigate,
}: {
  token: string;
  navigate: (r: Route, opts?: { replace?: boolean }) => void;
}) {
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    api
      .getShare(token)
      .then(data => {
        if (cancelled) return;
        // The server may return either the new nested {artists, sets}
        // shape or the legacy flat {slug: rating} map (for shares minted
        // before the set-level split). Normalise here.
        const picks: PreferenceMap = {};
        const setPicks: SetPreferenceMap = {};
        const raw = data.picks as
          | Record<string, Rating>
          | {
              artists?: Record<string, Rating>;
              sets?: Record<string, Rating>;
            };
        if (raw && typeof raw === "object" && ("artists" in raw || "sets" in raw)) {
          for (const [slug, r] of Object.entries(raw.artists ?? {})) {
            if (r === "like" || r === "superlike") picks[slug] = r;
          }
          for (const [idStr, r] of Object.entries(raw.sets ?? {})) {
            if (r === "like" || r === "superlike") {
              const n = Number(idStr);
              if (Number.isFinite(n)) setPicks[n] = r;
            }
          }
        } else {
          for (const [slug, r] of Object.entries(raw as Record<string, Rating>)) {
            if (r === "like" || r === "superlike") picks[slug] = r;
          }
        }
        setState({ kind: "loaded", eventSlug: data.eventSlug, picks });
        // Stash the imported picks in sessionStorage so the Schedule
        // view can render its import banner without stuffing them into
        // the URL.
        try {
          sessionStorage.setItem(
            "checkthelineup:pendingImport",
            JSON.stringify({
              token,
              eventSlug: data.eventSlug,
              picks,
              setPicks,
              name: data.name ?? null,
            }),
          );
        } catch { /* ignore */ }
        // Navigate to the schedule view for that event; replace so the
        // browser's Back button returns to whatever referred them here.
        navigate(
          {
            name: "event",
            eventSlug: data.eventSlug,
            tab: "schedule",
            artistSlug: null,
          },
          { replace: true },
        );
      })
      .catch(err => {
        if (cancelled) return;
        const msg = String(err ?? "");
        if (msg.includes("404")) setState({ kind: "notfound" });
        else setState({ kind: "error", message: msg });
      });
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  return (
    <div data-testid="view-share-landing">
      <div className="section-head">
        <div>
          <span className="super">SHARED LINEUP</span>
          <h2>
            {state.kind === "loading" && "Loading…"}
            {state.kind === "loaded" && "Loaded — redirecting…"}
            {state.kind === "notfound" && "Not found"}
            {state.kind === "error" && "Couldn't load"}
          </h2>
        </div>
      </div>
      <div className="empty">
        {state.kind === "loading" && (
          <>
            <div className="emoji">🎧</div>
            Pulling up the shared lineup…
          </>
        )}
        {state.kind === "notfound" && (
          <>
            <div className="emoji">🤷</div>
            This shared link has expired or doesn't exist.
            <div style={{ marginTop: 14 }}>
              <button
                className="chip"
                onClick={() => navigate({ name: "events" })}
              >
                Browse festivals
              </button>
            </div>
          </>
        )}
        {state.kind === "error" && (
          <>
            <div className="emoji">⚠️</div>
            Couldn't load shared lineup: {state.message}
            <div style={{ marginTop: 14 }}>
              <button
                className="chip"
                onClick={() => navigate({ name: "events" })}
              >
                Browse festivals
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
